import React, { SyntheticEvent, useCallback } from 'react'

import styled from '@emotion/styled'
import { isCaloriePreferenceSelected } from '@library/api-menu-experience'
import type { MenuPreferences } from '@library/api-menu-experience'
import { cssRecipeTile, cssRecipeTileContainer } from './styles'
import { SwapAlternativeOptions } from '../SwapAlternativeOptions/SwapAlternativeOptions'
import { CollectionUUID, Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'
import { AlternativeOptions } from '../../model/alternativeOptions/alternativeOptions'
import { RecipeTileHeader } from '../RecipeTileHeader'
import { getDietaryClaims } from '../../utils/getRecipeDietaryClaims'
import { getRecipeSubtitleItems } from '../RecipeSubtitle'
import { getCookingTimeLabel } from '../CookingTimeText'

const OuterContainer = styled.div(cssRecipeTile)

const TileContainer = styled.div(() => ({
  ...cssRecipeTileContainer,
}))

const dietaryLabels: Record<string, string> = {
  vegan: 'Plant based',
  vegetarian: 'Vegetarian',
  dairy_free: 'Dairy free',
  gluten_free: 'Gluten free',
}

const getRecipeTileAriaLabel = ({
  recipe,
  numPortions,
  menuPreferences,
  showCaloriesText,
  showCustomisedTileFooter,
}: {
  recipe: Recipe
  numPortions: number
  menuPreferences?: MenuPreferences
  showCaloriesText: boolean
  showCustomisedTileFooter: boolean
}) => {
  const announcementParts = [recipe.name]

  getDietaryClaims(recipe).forEach((claim) => {
    announcementParts.push(dietaryLabels[claim] ?? claim)
  })

  if (showCustomisedTileFooter) {
    getRecipeSubtitleItems({ recipe, numPortions, menuPreferences }).forEach((item) => {
      announcementParts.push(item.accessibleText)
    })
  } else {
    const cookingTimeLabel = getCookingTimeLabel({ recipe, numPortions })
    if (cookingTimeLabel) {
      announcementParts.push(cookingTimeLabel)
    }

    const caloriesPerPortion = recipe.nutritionalInformation.perPortion.energyKcal
    if (showCaloriesText && caloriesPerPortion) {
      announcementParts.push(`${caloriesPerPortion} calories`)
    }
  }

  return announcementParts.join('. ')
}

export type RecipeTileProps = {
  recipe: Recipe
  currentCollectionId: CollectionUUID
  menuId: string | null
  onClick: (recipeId: RecipeUUID) => void
  menuPreferences?: MenuPreferences
  setBrowserCTAVisible: () => void
  canAddRecipes: boolean
  addRecipe: (recipeId: RecipeUUID) => void
  removeRecipe: (recipeId: RecipeUUID) => void
  reachedLimit: boolean
  isInBasket: boolean
  isOutOfStock: boolean
  selectVariant: ({
    variant,
    variantOutOfStock,
    originalRecipeId,
    recipeReference,
  }: {
    variant: Recipe
    variantOutOfStock: boolean
    originalRecipeId: RecipeCoreId
    recipeReference: string
  }) => void
  alternativeOptions: AlternativeOptions
  onOpenAlternativeOptions?: () => void
  isLoadingOptions?: boolean
  recipeReference: string
  originalId: RecipeCoreId
  lazy?: boolean
  insideOverflow?: boolean
  index?: number
  quantity?: number
  showCustomisationEducation?: boolean
  closeCustomisationEducation?: () => void
  showCustomisedTileFooter?: boolean
  numPortions: number
}

export const RecipeTile = ({
  recipe,
  currentCollectionId: categoryId,
  menuId,
  onClick,
  menuPreferences,
  setBrowserCTAVisible,
  canAddRecipes,
  addRecipe,
  removeRecipe,
  reachedLimit,
  isInBasket,
  isOutOfStock,
  selectVariant,
  alternativeOptions,
  isLoadingOptions,
  onOpenAlternativeOptions,
  recipeReference,
  originalId,
  lazy = true,
  insideOverflow = false,
  index,
  quantity = 0,
  showCustomisationEducation = false,
  closeCustomisationEducation = undefined,
  showCustomisedTileFooter = false,
  numPortions,
}: RecipeTileProps) => {
  const { coreId: recipeCoreId, id: recipeUuid } = recipe
  const showCaloriesText =
    !menuPreferences || isCaloriePreferenceSelected(menuPreferences.display?.list)

  const selectRecipeVariant = useCallback(
    ({ variant, variantOutOfStock }: { variant: Recipe; variantOutOfStock: boolean }) => {
      selectVariant({
        variant,
        variantOutOfStock,
        originalRecipeId: originalId,
        recipeReference,
      })
    },
    [originalId, recipeReference, selectVariant],
  )

  // should never happen but caters for loading state
  if (categoryId === null) {
    return null
  }

  const handleOnClick = (e: SyntheticEvent) => {
    e.stopPropagation()
    onClick(recipeUuid)
  }

  return (
    <OuterContainer
      role="button"
      tabIndex={0}
      aria-label={getRecipeTileAriaLabel({
        recipe,
        numPortions,
        menuPreferences,
        showCaloriesText,
        showCustomisedTileFooter,
      })}
      data-testing={isOutOfStock ? 'menuRecipeOutOfStock' : 'menuRecipeViewDetails'}
      data-testing-id={recipeCoreId}
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
    >
      <TileContainer>
        <RecipeTileHeader
          recipe={recipe}
          currentCollectionId={categoryId}
          menuPreferences={menuPreferences}
          showCustomisedTileFooter={showCustomisedTileFooter}
          setBrowserCTAVisible={setBrowserCTAVisible}
          canAddRecipes={canAddRecipes}
          addRecipe={addRecipe}
          removeRecipe={removeRecipe}
          reachedLimit={reachedLimit}
          isInBasket={isInBasket}
          isOutOfStock={isOutOfStock}
          lazy={lazy}
          insideOverflow={insideOverflow}
          index={index}
          showPurchaseInfo={true}
          numPortions={numPortions}
          alternativeOptions={alternativeOptions}
        />
        <SwapAlternativeOptions
          currentRecipeCoreId={recipeCoreId}
          currentCollectionId={categoryId}
          menuId={menuId}
          menuPreferences={menuPreferences}
          recipe={recipe}
          originalId={originalId}
          reachedLimit={reachedLimit}
          selectVariant={selectRecipeVariant}
          addRecipeToBasket={addRecipe}
          alternativeOptions={alternativeOptions}
          onOpenAlternativeOptions={onOpenAlternativeOptions}
          isLoading={isLoadingOptions || false}
          canAddRecipes={canAddRecipes}
          removeRecipe={removeRecipe}
          quantity={quantity}
          recipeIndex={index}
          showCustomisationEducation={showCustomisationEducation}
          closeCustomisationEducation={closeCustomisationEducation}
        />
      </TileContainer>
    </OuterContainer>
  )
}
